export const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export function parseSlotData(
  serviceAvailabilityData?: string,
): Record<string, boolean | string | number | undefined> | null {
  if (!serviceAvailabilityData) return null;
  try {
    const parsed = JSON.parse(serviceAvailabilityData);
    return Array.isArray(parsed) ? parsed[0] : parsed;
  } catch {
    return null;
  }
}

export function isDayOpen(
  date: Date,
  serviceAvailability: number,
  serviceAvailabilityData?: string,
): boolean {
  if (serviceAvailability === 0 || !serviceAvailabilityData) return true;
  const slot = parseSlotData(serviceAvailabilityData);
  if (!slot) return true;

  const dayKey = WEEKDAY_KEYS[date.getDay()] ?? "sunday";
  const val = slot[dayKey];
  return val === true || val === "true" || val === 1;
}

const timeToMinutes = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

export function isTimeInSlot(
  time: string,
  date: Date,
  serviceAvailability: number,
  serviceAvailabilityData?: string,
): boolean {
  if (serviceAvailability === 0 || !serviceAvailabilityData) return true;
  const slot = parseSlotData(serviceAvailabilityData);
  if (!slot) return true;

  const dayKey = WEEKDAY_KEYS[date.getDay()] ?? "sunday";
  const t = timeToMinutes(time);
  const s1 = slot[`${dayKey}StartTime`] as string | undefined;
  const e1 = slot[`${dayKey}EndTime`] as string | undefined;
  const s2 = slot[`${dayKey}SecondStartTime`] as string | undefined;
  const e2 = slot[`${dayKey}SecondEndTime`] as string | undefined;

  const inSlot1 =
    s1 && e1 ? t >= timeToMinutes(s1) && t <= timeToMinutes(e1) : false;
  const inSlot2 =
    s2 && e2 ? t >= timeToMinutes(s2) && t <= timeToMinutes(e2) : false;
  return inSlot1 || inSlot2;
}

export function isDateDisabled(
  date: Date,
  serviceAvailability: number,
  serviceAvailabilityData?: string,
  maxDailyQuantity?: number | null,
  bookedDailyQuantities?: Record<string, number>,
): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  if (checkDate < today) return true;

  if (!isDayOpen(date, serviceAvailability, serviceAvailabilityData))
    return true;

  if (
    maxDailyQuantity != null &&
    maxDailyQuantity > 0 &&
    bookedDailyQuantities
  ) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    const booked = bookedDailyQuantities[dateStr] || 0;
    if (booked >= maxDailyQuantity) return true;
  }

  return false;
}

export function getFirstAvailableDateAndTime(
  minDate: Date,
  serviceAvailability: number,
  serviceAvailabilityData?: string,
  maxDailyQuantity?: number | null,
  bookedDailyQuantities?: Record<string, number>,
): { startDate: Date; startTime: string; endDate: Date; endTime: string } {
  const startDate = new Date(minDate);
  startDate.setHours(0, 0, 0, 0);

  if (serviceAvailability === 0 || !serviceAvailabilityData) {
    const endDate = new Date(startDate);
    return {
      startDate,
      startTime: "10:00",
      endDate,
      endTime: "11:00",
    };
  }

  const slot = parseSlotData(serviceAvailabilityData);
  if (!slot) {
    const endDate = new Date(startDate);
    return {
      startDate,
      startTime: "10:00",
      endDate,
      endTime: "11:00",
    };
  }

  // Cerca il primo giorno aperto entro 30 giorni che abbia posti disponibili
  for (let i = 0; i < 30; i++) {
    const candidateDate = new Date(startDate);
    candidateDate.setDate(startDate.getDate() + i);

    const dayKey = WEEKDAY_KEYS[candidateDate.getDay()] ?? "sunday";
    const val = slot[dayKey];
    const open = val === true || val === "true" || val === 1;

    if (open) {
      if (
        maxDailyQuantity != null &&
        maxDailyQuantity > 0 &&
        bookedDailyQuantities
      ) {
        const year = candidateDate.getFullYear();
        const month = String(candidateDate.getMonth() + 1).padStart(2, "0");
        const day = String(candidateDate.getDate()).padStart(2, "0");
        const dateStr = `${year}-${month}-${day}`;
        const booked = bookedDailyQuantities[dateStr] || 0;
        if (booked >= maxDailyQuantity) continue; // Sold out for this day, try next
      }

      const s1 = slot[`${dayKey}StartTime`] as string | undefined;
      const e1 = slot[`${dayKey}EndTime`] as string | undefined;
      const s2 = slot[`${dayKey}SecondStartTime`] as string | undefined;
      const e2 = slot[`${dayKey}SecondEndTime`] as string | undefined;

      const startTime = s1 ?? s2 ?? "10:00";
      const endTime = e1 ?? e2 ?? "11:00";

      const endDate = new Date(candidateDate);
      return {
        startDate: candidateDate,
        startTime,
        endDate,
        endTime,
      };
    }
  }

  const endDate = new Date(startDate);
  return {
    startDate,
    startTime: "10:00",
    endDate,
    endTime: "11:00",
  };
}
